<template>
    <div class="lease-key-dates">
        <!-- Breadcrumb / Title -->
        <div class="lease-key-dates__breadcrumb">
            <span v-if="!editingKeyDate && !creatingKeyDate">Key Dates</span>
            <div v-else class="lease-key-dates__breadcrumb-nav">
                <a @click="cancelEdit" class="lease-key-dates__breadcrumb-link">Key Dates</a>
                <span class="lease-key-dates__breadcrumb-separator">/</span>
                <span v-if="creatingKeyDate">Create New Key Date</span>
                <span v-if="editingKeyDate">Edit Key Date</span>
            </div>
        </div>

        <!-- Key Dates List -->
        <div v-if="!editingKeyDate && !creatingKeyDate" class="lease-key-dates__list">
            <div v-if="!leases.selectedLease?.key_dates?.length" class="lease-key-dates__empty">
                <span class="lease-key-dates__empty-text">No Key Dates Found</span>
                <themed-button
                    v-if="canCreateKeyDate"
                    variant="default"
                    @click="createKeyDate"
                >
                    <theme-icon icon="add-icon" type="icons" classes="w-4 h-4" />
                    <span>New Key Date</span>
                </themed-button>
            </div>

            <div v-else>
                <themed-table
                    :columns="columns"
                    :data="sortedKeyDates"
                    :pagination="false"
                    :searchable="false"
                    :sortable="false"
                    hoverable
                    @action="handleAction"
                />

                <themed-button
                    v-if="canCreateKeyDate"
                    variant="default"
                    size="sm"
                    @click="createKeyDate"
                    class="lease-key-dates__create-button"
                >
                    <theme-icon icon="add" type="icons" classes="w-4 h-4" />
                    <span>Create Key Date</span>
                </themed-button>
            </div>
        </div>

        <!-- Key Date Form (Edit/Create) -->
        <div v-else-if="keyDate" class="lease-key-dates__form">
            <div class="lease-key-dates__form-content">
                <div class="lease-key-dates__form-main">
                    <div class="lease-key-dates__fields">
                        <div class="lease-key-dates__field">
                            <label for="occurs_on">Occurs On</label>
                            <themed-date-picker
                                id="occurs_on"
                                :value="keyDate.occurs_on"
                                @input="updateField('occurs_on', $event)"
                                :error="Boolean(errors.occurs_on)"
                            />
                            <div v-if="errors.occurs_on" class="lease-key-dates__error">
                                {{ errors.occurs_on[0] }}
                            </div>
                        </div>

                        <div class="lease-key-dates__field">
                            <label for="type">Type</label>
                            <themed-select
                                id="type"
                                :value="keyDate.type"
                                :options="typeOptions"
                                @input="updateField('type', $event)"
                                placeholder="Select type"
                                :error="Boolean(errors.type)"
                            />
                            <div v-if="errors.type" class="lease-key-dates__error">
                                {{ errors.type[0] }}
                            </div>
                        </div>

                        <div class="lease-key-dates__field">
                            <label for="lease_clause">Clause</label>
                            <themed-input
                                id="lease_clause"
                                :value="keyDate.lease_clause"
                                @input="updateField('lease_clause', $event)"
                                :error="Boolean(errors.lease_clause)"
                            />
                            <div v-if="errors.lease_clause" class="lease-key-dates__error">
                                {{ errors.lease_clause[0] }}
                            </div>
                        </div>
                    </div>

                    <div class="lease-key-dates__note">
                        <themed-textarea
                            :value="keyDate.note"
                            @input="updateField('note', $event)"
                            placeholder="Key Date Notes"
                            :rows="5"
                        />
                    </div>
                </div>

                <div class="lease-key-dates__sidebar">
                    <div class="lease-key-dates__toggle">
                        <label>Completed?</label>
                        <themed-toggle
                            :value="isCompleted"
                            @input="handleCompletedToggle"
                        />
                    </div>

                    <div v-if="isCompleted" class="lease-key-dates__completion">
                        <div class="lease-key-dates__field">
                            <label for="completed_at">On</label>
                            <themed-date-picker
                                id="completed_at"
                                :value="keyDate.completed_at"
                                @input="updateField('completed_at', $event)"
                            />
                        </div>

                        <div class="lease-key-dates__field">
                            <label for="completed_by">By</label>
                            <themed-select
                                id="completed_by"
                                :value="keyDate.completed_by"
                                :options="userOptions"
                                @input="updateField('completed_by', $event)"
                                placeholder="Select user"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import moment from 'moment';
import ThemedButton from '../ThemedButton.vue';
import ThemedTable from '../ThemedTable.vue';
import ThemedDatePicker from '../form/ThemedDatePicker.vue';
import ThemedSelect from '../ThemedSelect.vue';
import ThemedInput from '../ThemedInput.vue';
import ThemedTextarea from '../form/ThemedTextarea.vue';
import ThemedToggle from '../ThemedToggle.vue';

export default {
    name: 'LeaseKeyDates',

    components: {
        ThemedButton,
        ThemedTable,
        ThemedDatePicker,
        ThemedSelect,
        ThemedInput,
        ThemedTextarea,
        ThemedToggle
    },

    props: {
        formErrors: {
            type: [String, Object],
            default: () => ({})
        }
    },

    data() {
        return {
            editingKeyDate: false,
            creatingKeyDate: false,
            selectedKeyDateId: '',
            users: [],
            isCompleted: false
        };
    },

    computed: {
        ...mapState(['leases', 'sites']),

        canCreateKeyDate() {
            return this.$gate.can('create', 'site');
        },

        canUpdateKeyDate() {
            return this.$gate.can('update', 'site', this.leases.selectedLease?.site_id);
        },

        canDeleteKeyDate() {
            return this.$gate.can('delete', 'site', this.leases.selectedLease?.site_id);
        },

        keyDate() {
            if (!this.selectedKeyDateId) return null;
            return _.find(this.leases.selectedLease?.key_dates, ['id', this.selectedKeyDateId]);
        },

        sortedKeyDates() {
            const dates = this.leases.selectedLease?.key_dates || [];
            return _.orderBy(dates, ['occurs_on'], ['asc']);
        },

        errors() {
            return this.formErrors || {};
        },

        typeOptions() {
            return [
                { value: 'Expiration', label: 'Expiration' },
                { value: 'Renewal', label: 'Renewal' },
                { value: 'Cancellation', label: 'Cancellation' },
                { value: 'Expansion', label: 'Expansion' },
                { value: 'Notice of Renewal', label: 'Notice of Renewal' },
                { value: 'Option', label: 'Option' },
                { value: 'Status Change', label: 'Status Change' }
            ];
        },

        userOptions() {
            return _.orderBy(this.users, ['name']).map(user => ({
                value: user.id,
                label: user.name
            }));
        },

        columns() {
            return [
                {
                    field: 'occurs_on',
                    label: 'Occurs On',
                    align: 'center',
                    formatter: (value) => this.formatDate(value)
                },
                {
                    field: 'type',
                    label: 'Type'
                },
                {
                    field: 'lease_clause',
                    label: 'Clause',
                    align: 'center'
                },
                {
                    field: 'completed_at',
                    label: 'Status',
                    align: 'center',
                    formatter: (value) => {
                        if (!value) {
                            return '<span class="themed-badge themed-badge--destructive">Incomplete</span>';
                        }
                        return '<span class="themed-badge themed-badge--success">Completed</span>';
                    }
                },
                {
                    field: 'note',
                    label: 'Note',
                    formatter: (value) => {
                        if (!value) return '';
                        const truncated = value.length > 50 ? value.substring(0, 50) + '...' : value;
                        return `<span title="${value}">${truncated}</span>`;
                    }
                },
                {
                    field: 'actions',
                    label: '',
                    type: 'actions',
                    sortable: false,
                    buttons: [
                        {
                            name: 'edit',
                            label: 'Edit',
                            icon: 'edit',
                            variant: 'ghost',
                            size: 'sm',
                            iconOnly: true,
                            visible: () => this.canUpdateKeyDate
                        },
                        {
                            name: 'delete',
                            label: 'Delete',
                            icon: 'trash',
                            variant: 'ghost',
                            size: 'sm',
                            iconOnly: true,
                            visible: () => this.canDeleteKeyDate
                        }
                    ]
                }
            ];
        }
    },

    watch: {
        selectedKeyDateId(newId) {
            if (this.keyDate) {
                this.isCompleted = this.keyDate.completed_at != null;
            }
            this.$emit('selectedKeyDateChange', newId);
        },

        creatingKeyDate(creating) {
            if (!creating && this.selectedKeyDateId === 'new') {
                this.removeKeyDate('new');
            }
        },

        'leases.selectedLease.key_dates': {
            handler() {
                if (this.leases.selectedLease && this.creatingKeyDate) {
                    const newKeyDate = _.find(this.leases.selectedLease.key_dates, ['id', 'new']);
                    if (!newKeyDate) {
                        const lastKeyDate = _.last(this.leases.selectedLease.key_dates);
                        if (lastKeyDate) {
                            this.selectedKeyDateId = lastKeyDate.id;
                            this.creatingKeyDate = false;
                        }
                    }
                }
            },
            deep: true
        }
    },

    created() {
        this.$parent.$parent.$on('keyDateSaved', (keyDateId) => {
            this.selectedKeyDateId = keyDateId;
            this.creatingKeyDate = false;
            this.editingKeyDate = true;
        });
    },

    mounted() {
        this.loadUsers();
    },

    methods: {
        handleAction({ action, row }) {
            switch (action) {
                case 'edit':
                    this.editKeyDate(row.id);
                    break;
                case 'delete':
                    this.confirmDeleteKeyDate(row.id);
                    break;
            }
        },

        async loadUsers() {
            try {
                const response = await axios.get(`/api/clients/${window.user.client_id}/users`);
                this.users = response.data;
            } catch (error) {
                this.$toast?.error('Error loading users');
            }
        },

        createKeyDate() {
            if (!this.leases.selectedLease) {
                this.$toast?.error('No lease selected');
                return;
            }
            this.creatingKeyDate = true;
            this.editingKeyDate = false;

            if (!this.leases.selectedLease.key_dates) {
                this.leases.selectedLease.key_dates = [];
            }

            this.leases.selectedLease.key_dates.push({
                id: 'new',
                lease_id: this.leases.selectedLease.id,
                occurs_on: '',
                type: '',
                lease_clause: '',
                note: '',
                completed_at: null,
                completed_by: null
            });

            this.selectedKeyDateId = 'new';
        },

        editKeyDate(keyDateId) {
            this.selectedKeyDateId = keyDateId;
            this.creatingKeyDate = false;
            this.editingKeyDate = true;
        },

        cancelEdit() {
            this.creatingKeyDate = false;
            this.editingKeyDate = false;
            this.selectedKeyDateId = '';
            this.isCompleted = false;
        },

        updateField(field, value) {
            if (this.keyDate) {
                this.keyDate[field] = value;
            }
        },

        handleCompletedToggle(value) {
            this.isCompleted = value;
            if (!value) {
                this.updateField('completed_at', null);
                this.updateField('completed_by', null);
            }
        },

        removeKeyDate(keyDateId) {
            _.remove(this.leases.selectedLease.key_dates, (date) => date.id === keyDateId);
            this.$forceUpdate();
        },

        confirmDeleteKeyDate(keyDateId) {
            if (keyDateId === 'new') {
                this.removeKeyDate('new');
                return;
            }

            this.$swal({
                title: 'Are you sure?',
                text: 'Are you sure you want to delete this Key Date?',
                type: 'warning',
                buttonsStyling: false,
                confirmButtonText: 'Yes - Delete it',
                confirmButtonClass: 'btn btn-red',
                showCancelButton: true,
                cancelButtonClass: 'btn btn-secondary mr-4',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.deleteKeyDate(keyDateId);
                }
            });
        },

        async deleteKeyDate(keyDateId) {
            try {
                const response = await axios.delete(
                    `/leases/${this.leases.selectedLease.id}/key-dates/${keyDateId}`
                );
                this.$toast?.success(response.data.message || 'Key date deleted successfully');
                this.removeKeyDate(keyDateId);
                this.$eventHub.$emit('reloadSite');
            } catch (error) {
                this.$toast?.error(error.response?.data?.message || 'Failed to delete key date');
            }
        },

        formatDate(date) {
            return date ? moment(date).format('MM/DD/YYYY') : '';
        }
    }
};
</script>
